import { isDev } from '@myre/common-conf';

import {
  Directive,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  Renderer2,
} from '@angular/core';

const MAIN_APPLICATION_HEADER_HEIGHT = 40;
const CONTENT_CONTAINER = 'mat-sidenav-content';

export type FixPosition = 'top' | 'bottom';

@Directive({ selector: '[fixedOnScroll]' })
export class FixedOnScrollDirective {
  private readonly _elementRef = inject(ElementRef);
  private readonly _renderer = inject(Renderer2);

  /**
   * Position of the fixed element. Can be 'top' or 'bottom'.
   * If null, the element will not be fixed and the directive will not listen to scroll events.
   */
  @Input('fixedOnScroll')
  public set position(position: FixPosition | null) {
    if (this._position === position) {
      return;
    }

    this._position = position;

    if (this._position === null) {
      this._removeListeners();

      return;
    }

    this._addListener();
  }

  /**
   * Reference to the element that will be used to calculate the position of the fixed element.
   */
  @Input()
  public containerReference: HTMLElement;

  /**
   * (Default) If false, the width of the fixed element will match the width of itself.
   * If true, the width of the fixed element will match the width of the containerReference @Input.
   */
  @Input()
  public widthShouldMatchContainer = false;

  /**
   * Offset in pixels to apply to the fixed element.
   */
  @Input()
  public offset = 0;

  @Output('fixedStateChange')
  public stateChangeEmitter = new EventEmitter<{ fixed: boolean }>();

  private _position: FixPosition | null = null;
  private _isFixed = false;

  private _unlistenFct: (() => void) | null = null;

  private _onScroll(): void {
    // Current theoretical position of the host element of the directive. This allows us to know if it is theoretically in the viewport or not.
    // We calculate its theoretical position because the element can be fixed and therefore no longer be at the initial location.
    let position = 0;

    // Is the reference component completely outside the viewport?
    // If yes, we do not want to fix the host while the reference component is not visible.
    let outOfScope = false;

    switch (this._position) {
      case 'top':
        position =
          this.containerReference.getBoundingClientRect().top -
          this._elementRef.nativeElement.getBoundingClientRect().height -
          MAIN_APPLICATION_HEADER_HEIGHT;

        outOfScope =
          this.containerReference.getBoundingClientRect().bottom -
            MAIN_APPLICATION_HEADER_HEIGHT <
          0;

        this._manageFixingElement(
          position,
          outOfScope,
          MAIN_APPLICATION_HEADER_HEIGHT + this.offset,
        );

        break;
      case 'bottom':
        position =
          window.innerHeight -
          this.containerReference.getBoundingClientRect().bottom -
          this._elementRef.nativeElement.getBoundingClientRect().height;

        outOfScope =
          this.containerReference.getBoundingClientRect().top >
          window.innerHeight;

        this._manageFixingElement(position, outOfScope);

        break;
    }
  }

  private _manageFixingElement(
    position: number,
    outOfScope: boolean,
    space = 0,
  ): void {
    // We use two different conditions because we do not want to intervene on the element if it is already in the correct state.
    // We want to fix it if it's in the right condition AND if it is not already fixed. Conversely, for the unfix.
    const shouldFix = !this._isFixed && !outOfScope && position < 0;
    const shouldUnfix = this._isFixed && (position > 0 || outOfScope);

    if (!shouldFix && !shouldUnfix) {
      return;
    }

    if (shouldFix) {
      this._fixElement(space);
    } else {
      this._unfixElement();
    }

    this.stateChangeEmitter.emit({ fixed: shouldFix });
  }

  private _fixElement(space: number): void {
    const newWidth = this._getNewWidth();

    this._renderer.addClass(this._elementRef.nativeElement, 'fixed');
    this._renderer.addClass(this.containerReference, 'with-footer--fixed');

    this._setStyle(this._position, `${space}px`);
    this._setStyle('width', `${newWidth}px`);

    this._isFixed = true;
  }

  private _unfixElement(): void {
    this._renderer.removeClass(this._elementRef.nativeElement, 'fixed');
    this._renderer.removeClass(this.containerReference, 'with-footer--fixed');

    this._removeStyle(this._position);
    this._removeStyle('width');

    this._isFixed = false;
  }

  private _setStyle(style: string, value: string): void {
    this._renderer.setStyle(this._elementRef.nativeElement, style, value);
  }

  private _removeStyle(style: string): void {
    this._renderer.removeStyle(this._elementRef.nativeElement, style);
  }

  private _getNewWidth(): number {
    return this.widthShouldMatchContainer
      ? this.containerReference.getBoundingClientRect().width
      : this._elementRef.nativeElement.getBoundingClientRect().width;
  }

  private _removeListeners(): void {
    this._unlistenFct();
    this._unlistenFct = null;
  }

  private _addListener(): () => void {
    // Prevents adding listener multiple times
    if (this._unlistenFct) {
      return;
    }

    this._unlistenFct = this._renderer.listen(
      this._findDefaultContainerByTagName(),
      'scroll',
      () => this._onScroll(),
    );
  }

  /**
   * duplicate function with sticky.directive.ts, should be refactored
   */
  private _findDefaultContainerByTagName(): HTMLElement | null {
    let element = this._elementRef.nativeElement;

    while (element.parentElement) {
      if (element.parentElement.tagName.toLowerCase() === CONTENT_CONTAINER) {
        return element.parentElement;
      }

      element = element.parentElement;
    }

    if (isDev()) {
      // eslint-disable-next-line no-console
      console.error(
        `[StickyDirective] No container found for ${CONTENT_CONTAINER}`,
      );
    }

    return null;
  }
}
